import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import api from '../../../api.js';

function OrganizationFormModal({ isOpen, onClose, onSuccess, onMessage, editingOrg, organizations }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    parent_org_id: '',
    name: '',
    legal_name: '',
    org_type: 'standalone',
    tax_id: '',
    tax_id_type: 'siret',
    address: '',
    postal_code: '',
    city: '',
    country: 'FR',
    phone: '',
    email: '',
    website: '',
    fleet_size_limit: '',
    status: 'active',
  });

  useEffect(() => {
    if (editingOrg) {
      setFormData({
        code: editingOrg.code || '',
        parent_org_id: editingOrg.parent_org_id || '',
        name: editingOrg.name || '',
        legal_name: editingOrg.legal_name || '',
        org_type: editingOrg.org_type || 'standalone',
        tax_id: editingOrg.tax_id || '',
        tax_id_type: editingOrg.tax_id_type || 'siret',
        address: editingOrg.address || '',
        postal_code: editingOrg.postal_code || '',
        city: editingOrg.city || '',
        country: editingOrg.country || 'FR',
        phone: editingOrg.phone || '',
        email: editingOrg.email || '',
        website: editingOrg.website || '',
        fleet_size_limit: editingOrg.fleet_size_limit || '',
        status: editingOrg.status || 'active',
      });
    }
  }, [editingOrg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (submitData.fleet_size_limit === '') delete submitData.fleet_size_limit;
      if (submitData.parent_org_id === '') submitData.parent_org_id = null;

      if (editingOrg) {
        await api.organization.update(editingOrg.id, submitData);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.orgForm.successUpdate'), type: 'success' });
      } else {
        await api.organization.create(submitData);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.orgForm.successCreate'), type: 'success' });
      }
      onSuccess();
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.orgForm.errorPrefix', { message: error.message }), type: 'error' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {editingOrg ? t('settings.orgForm.editTitle') : t('settings.orgForm.createTitle')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.codeLabel')}</label>
              <input
                type="text"
                required
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={t('settings.orgForm.codePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.typeLabel')}</label>
              <select
                value={formData.org_type}
                onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="standalone">{t('settings.orgForm.orgTypes.standalone')}</option>
                <option value="holding">{t('settings.orgForm.orgTypes.holding')}</option>
                <option value="subsidiary">{t('settings.orgForm.orgTypes.subsidiary')}</option>
                <option value="branch">{t('settings.orgForm.orgTypes.branch')}</option>
                <option value="division">{t('settings.orgForm.orgTypes.division')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.nameLabel')}</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.legalNameLabel')}</label>
              <input
                type="text"
                value={formData.legal_name}
                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {/* Hierarchy */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.parentLabel')}</label>
            <select
              value={formData.parent_org_id}
              onChange={(e) => setFormData({ ...formData, parent_org_id: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="">{t('settings.orgForm.parentNoneOption')}</option>
              {organizations.filter(o => o.id !== editingOrg?.id).map(org => (
                <option key={org.id} value={org.id}>{org.name} ({org.code})</option>
              ))}
            </select>
          </div>
          {/* Fleet Size Limit removed - managed only via Commercial Cockpit by superusers */}

          {/* Tax Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.taxIdLabel')}</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.taxIdTypeLabel')}</label>
              <select
                value={formData.tax_id_type}
                onChange={(e) => setFormData({ ...formData, tax_id_type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="siret">{t('settings.orgForm.taxIdTypes.siret')}</option>
                <option value="siren">{t('settings.orgForm.taxIdTypes.siren')}</option>
                <option value="vat">{t('settings.orgForm.taxIdTypes.vat')}</option>
                <option value="ein">{t('settings.orgForm.taxIdTypes.ein')}</option>
                <option value="other">{t('settings.orgForm.taxIdTypes.other')}</option>
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.addressLabel')}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.postalCodeLabel')}</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.cityLabel')}</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.countryLabel')}</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.phoneLabel')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.emailLabel')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.websiteLabel')}</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.orgForm.statusLabel')}</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="active">{t('settings.orgForm.orgStatus.active')}</option>
              <option value="suspended">{t('settings.orgForm.orgStatus.suspended')}</option>
              <option value="closed">{t('settings.orgForm.orgStatus.closed')}</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition"
            >
              {editingOrg ? t('common.actions.update') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrganizationFormModal;
